import type { UseFormSetError } from 'react-hook-form';
import { toast } from 'react-toastify/unstyled';

import { useRequestNewInquiryMutation, useRequestNewTraceMutation } from '@features/investigations/api/files.api';
import type { NewInvestigationFormValues } from '@features/investigations/components/modals/add-new-investigation/investigation.schema';
import { mapFormValuesToPayload } from '@features/investigations/components/modals/add-new-investigation/investigation.utils';
import type { RTKQueryError } from '@shared/utils/api.types';

// TODO(file-launch-renaming): rename this hook to neutral file-launch naming (currently handles investigation + inquiry).
export const useAddNewTrace = () => {
  const [requestNewTraceMutation, { isLoading: isTraceLoading }] = useRequestNewTraceMutation();
  const [requestNewInquiryMutation, { isLoading: isInquiryLoading }] = useRequestNewInquiryMutation();

  const submitNewTrace = async (
    formData: NewInvestigationFormValues,
    setError: UseFormSetError<NewInvestigationFormValues>,
  ) => {
    // TODO(file-launch-renaming): rename submit function away from legacy "Trace" wording.
    const isInvestigation = formData.type === 'INVESTIGATION';

    try {
      const payload = mapFormValuesToPayload(formData);
      await (isInvestigation ? requestNewTraceMutation : requestNewInquiryMutation)(payload).unwrap();
      toast.success(`${isInvestigation ? 'Investigation' : 'Inquiry'} requested successfully`);
    } catch (err) {
      console.error('Submission failed:', err);

      const errorObj = err as RTKQueryError;
      const backendErrors = errorObj?.data?.errors ?? {};

      if (backendErrors.dateTo ?? backendErrors.dateFrom) {
        setError('dateRange', {
          type: 'server',
          message: backendErrors.dateTo ?? backendErrors.dateFrom,
        });
      }

      if (backendErrors.traceName) {
        setError('traceName', {
          type: 'server',
          message: backendErrors.traceName,
        });
      }

      if (backendErrors.userName ?? backendErrors.user_name) {
        setError('inquiryUsername', {
          type: 'server',
          message: backendErrors.userName ?? backendErrors.user_name,
        });
      }

      toast.error(errorObj?.data?.message ?? `Error creating new ${isInvestigation ? 'investigation' : 'inquiry'}`);
      throw err;
    }
  };

  return {
    submitNewTrace,
    isLoading: isTraceLoading || isInquiryLoading,
  };
};
